import { generateBoard, terrainResource } from './board.js';
import { updateLongestRoad } from './longestRoad.js';
import { Rng } from './rng.js';
import {
  Action,
  DevCardType,
  GameError,
  GameState,
  PLAYER_COUNT,
  Player,
  RESOURCES,
  Resource,
  ResourceCounts,
  WINNING_POINTS,
} from './types.js';

export const COSTS = {
  road: { wood: 1, brick: 1 },
  settlement: { wood: 1, brick: 1, sheep: 1, wheat: 1 },
  city: { wheat: 2, ore: 3 },
  devCard: { sheep: 1, wheat: 1, ore: 1 },
} satisfies Record<string, Partial<ResourceCounts>>;

export const PLAYER_COLORS = ['#d9453b', '#2f6fd6', '#e8a33a', '#3a9e5c'];

const DEV_DECK: DevCardType[] = [
  ...Array<DevCardType>(14).fill('knight'),
  ...Array<DevCardType>(5).fill('victoryPoint'),
  ...Array<DevCardType>(2).fill('roadBuilding'),
  ...Array<DevCardType>(2).fill('yearOfPlenty'),
  ...Array<DevCardType>(2).fill('monopoly'),
];

// ---------- resource helpers ----------

export function emptyResources(): ResourceCounts {
  return { wood: 0, brick: 0, sheep: 0, wheat: 0, ore: 0 };
}

export function toCounts(partial: Partial<ResourceCounts>): ResourceCounts {
  const out = emptyResources();
  for (const r of RESOURCES) {
    const n = partial[r] ?? 0;
    if (!Number.isInteger(n) || n < 0) throw new GameError(`Invalid amount of ${r}.`);
    out[r] = n;
  }
  return out;
}

export function totalCards(rc: Partial<ResourceCounts>): number {
  return RESOURCES.reduce((sum, r) => sum + (rc[r] ?? 0), 0);
}

function hasAll(have: ResourceCounts, need: Partial<ResourceCounts>): boolean {
  return RESOURCES.every((r) => have[r] >= (need[r] ?? 0));
}

function pay(state: GameState, player: number, cost: Partial<ResourceCounts>, what: string): void {
  const p = state.players[player];
  if (!hasAll(p.resources, cost)) throw new GameError(`Not enough resources for ${what}.`);
  for (const r of RESOURCES) {
    const n = cost[r] ?? 0;
    p.resources[r] -= n;
    state.bank[r] += n;
  }
}

function describe(rc: Partial<ResourceCounts>): string {
  const parts = RESOURCES.filter((r) => (rc[r] ?? 0) > 0).map((r) => `${rc[r]} ${r}`);
  return parts.length ? parts.join(', ') : 'nothing';
}

// ---------- setup ----------

export interface NewGameOptions {
  names: string[];
  seed?: number;
}

export function createGame({ names, seed }: NewGameOptions): GameState {
  if (names.length !== PLAYER_COUNT) throw new GameError(`This game is for exactly ${PLAYER_COUNT} players.`);
  const rng = new Rng(seed ?? Math.floor(Math.random() * 2 ** 31));
  const board = generateBoard(rng);
  const first = rng.int(PLAYER_COUNT);
  const round = [0, 1, 2, 3].map((i) => (first + i) % PLAYER_COUNT);

  const players: Player[] = names.map((name, i) => ({
    name,
    color: PLAYER_COLORS[i],
    resources: emptyResources(),
    devCards: [],
    knightsPlayed: 0,
    roadsLeft: 15,
    settlementsLeft: 5,
    citiesLeft: 4,
  }));

  return {
    board,
    players,
    buildings: {},
    roads: {},
    robberHex: board.hexes.find((h) => h.terrain === 'desert')!.id,
    bank: { wood: 19, brick: 19, sheep: 19, wheat: 19, ore: 19 },
    devDeck: rng.shuffle(DEV_DECK),
    phase: 'setup',
    setup: { order: [...round, ...round.slice().reverse()], index: 0, step: 'settlement', lastSettlement: null },
    currentPlayer: first,
    turn: 0,
    turnStep: 'roll',
    afterRobber: 'actions',
    pendingDiscards: {},
    dice: null,
    devCardPlayedThisTurn: false,
    freeRoads: 0,
    trade: null,
    longestRoad: null,
    largestArmy: null,
    winner: null,
    log: [`${names[first]} goes first.`],
    rngState: rng.state,
  };
}

// ---------- placement rules ----------

/** Empty vertex with no building on any neighboring vertex. */
function isOpenVertex(state: GameState, vertex: number): boolean {
  const v = state.board.vertices[vertex];
  if (!v || state.buildings[vertex]) return false;
  return v.neighbors.every((n) => !state.buildings[n]);
}

function touchesOwnRoad(state: GameState, player: number, vertex: number): boolean {
  return state.board.vertices[vertex].edges.some((e) => state.roads[e] === player);
}

export function canPlaceSettlement(state: GameState, player: number, vertex: number, needsRoad: boolean): boolean {
  return isOpenVertex(state, vertex) && (!needsRoad || touchesOwnRoad(state, player, vertex));
}

export function canPlaceRoad(state: GameState, player: number, edge: number): boolean {
  const e = state.board.edges[edge];
  if (!e || state.roads[edge] !== undefined) return false;
  return e.vertices.some((v) => {
    const b = state.buildings[v];
    if (b) return b.player === player;
    return state.board.vertices[v].edges.some((other) => other !== edge && state.roads[other] === player);
  });
}

export function validSettlementSpots(state: GameState, player: number): number[] {
  const needsRoad = state.phase !== 'setup';
  return state.board.vertices.map((v) => v.id).filter((v) => canPlaceSettlement(state, player, v, needsRoad));
}

export function validRoadSpots(state: GameState, player: number): number[] {
  if (state.phase === 'setup') {
    const last = state.setup.lastSettlement;
    if (last === null) return [];
    return state.board.vertices[last].edges.filter((e) => state.roads[e] === undefined);
  }
  return state.board.edges.map((e) => e.id).filter((e) => canPlaceRoad(state, player, e));
}

export function validCitySpots(state: GameState, player: number): number[] {
  return Object.entries(state.buildings)
    .filter(([, b]) => b.player === player && b.type === 'settlement')
    .map(([v]) => Number(v));
}

/** Best bank ratio for trading away `resource`: 4, 3 (generic harbor), or 2 (matching harbor). */
export function tradeRatio(state: GameState, player: number, resource: Resource): number {
  let ratio = 4;
  for (const [v, b] of Object.entries(state.buildings)) {
    if (b.player !== player) continue;
    const h = state.board.vertices[Number(v)].harbor;
    if (h === null) continue;
    const kind = state.board.harbors[h].kind;
    if (kind === resource) ratio = Math.min(ratio, 2);
    else if (kind === 'any') ratio = Math.min(ratio, 3);
  }
  return ratio;
}

export function robberVictims(state: GameState, player: number, hex: number): number[] {
  const victims = new Set<number>();
  for (const v of state.board.hexes[hex].vertices) {
    const b = state.buildings[v];
    if (b && b.player !== player && totalCards(state.players[b.player].resources) > 0) victims.add(b.player);
  }
  return [...victims].sort();
}

// ---------- scoring ----------

/** Victory points. Hidden victory point cards are only counted when `includeHidden` is set. */
export function victoryPoints(state: GameState, player: number, includeHidden = true): number {
  let points = 0;
  for (const b of Object.values(state.buildings)) {
    if (b.player === player) points += b.type === 'city' ? 2 : 1;
  }
  if (state.longestRoad?.player === player) points += 2;
  if (state.largestArmy?.player === player) points += 2;
  if (includeHidden) points += state.players[player].devCards.filter((c) => c.type === 'victoryPoint').length;
  return points;
}

function checkWin(state: GameState): void {
  if (state.phase !== 'main') return;
  const p = state.currentPlayer;
  if (victoryPoints(state, p) >= WINNING_POINTS) {
    state.phase = 'ended';
    state.winner = p;
    state.log.push(`${state.players[p].name} wins with ${victoryPoints(state, p)} points!`);
  }
}

// ---------- production ----------

function produce(state: GameState, roll: number): void {
  const demand: Record<Resource, number[]> = {
    wood: [0, 0, 0, 0],
    brick: [0, 0, 0, 0],
    sheep: [0, 0, 0, 0],
    wheat: [0, 0, 0, 0],
    ore: [0, 0, 0, 0],
  };
  for (const hex of state.board.hexes) {
    if (hex.token !== roll || hex.id === state.robberHex) continue;
    const r = terrainResource(hex.terrain);
    if (!r) continue;
    for (const v of hex.vertices) {
      const b = state.buildings[v];
      if (b) demand[r][b.player] += b.type === 'city' ? 2 : 1;
    }
  }
  const gains = state.players.map(() => emptyResources());
  for (const r of RESOURCES) {
    const wanted = demand[r];
    const total = wanted.reduce((a, b) => a + b, 0);
    if (total === 0) continue;
    const claimants = wanted.filter((n) => n > 0).length;
    if (total <= state.bank[r]) {
      wanted.forEach((n, p) => (gains[p][r] += n));
    } else if (claimants === 1) {
      const p = wanted.findIndex((n) => n > 0);
      gains[p][r] += state.bank[r];
      state.log.push(`The bank is short of ${r}.`);
    } else {
      state.log.push(`The bank is short of ${r}, so nobody gets any.`);
      continue;
    }
  }
  gains.forEach((g, p) => {
    if (totalCards(g) === 0) return;
    for (const r of RESOURCES) {
      state.players[p].resources[r] += g[r];
      state.bank[r] -= g[r];
    }
    state.log.push(`${state.players[p].name} collects ${describe(g)}.`);
  });
}

// ---------- action handling ----------

function requireTurn(state: GameState, player: number): void {
  if (state.currentPlayer !== player) throw new GameError(`It's not your turn.`);
}

function requireStep(state: GameState, ...steps: GameState['turnStep'][]): void {
  if (state.phase !== 'main') throw new GameError(`The game isn't in progress.`);
  if (!steps.includes(state.turnStep)) {
    const hint: Record<GameState['turnStep'], string> = {
      roll: 'roll the dice first',
      discard: 'wait for players to discard',
      moveRobber: 'move the robber first',
      actions: 'you have already rolled',
    };
    throw new GameError(`You can't do that now: ${hint[state.turnStep]}.`);
  }
}

function takeDevCard(state: GameState, player: number, type: DevCardType): void {
  if (state.devCardPlayedThisTurn) throw new GameError(`You can only play one development card per turn.`);
  const cards = state.players[player].devCards;
  const i = cards.findIndex((c) => c.type === type && c.boughtOnTurn < state.turn);
  if (i < 0) throw new GameError(`You don't have a ${type} card you can play this turn.`);
  cards.splice(i, 1);
  state.devCardPlayedThisTurn = true;
}

/**
 * Apply an action and return the new state. The input state is not modified.
 * Throws GameError if the action is not allowed.
 */
export function applyAction(prev: GameState, action: Action): GameState {
  if (prev.phase === 'ended') throw new GameError(`The game is over.`);
  if (!Number.isInteger(action.player) || action.player < 0 || action.player >= PLAYER_COUNT) {
    throw new GameError(`Unknown player.`);
  }
  const state = structuredClone(prev);
  const rng = new Rng(state.rngState);
  handle(state, action, rng);
  state.rngState = rng.state;
  checkWin(state);
  return state;
}

function handle(state: GameState, action: Action, rng: Rng): void {
  const me = action.player;
  const player = state.players[me];
  const name = player.name;

  switch (action.type) {
    case 'placeSettlement': {
      if (state.phase !== 'setup' || state.setup.step !== 'settlement') throw new GameError(`Place a road now.`);
      requireTurn(state, me);
      if (!canPlaceSettlement(state, me, action.vertex, false)) throw new GameError(`You can't build there.`);
      state.buildings[action.vertex] = { player: me, type: 'settlement' };
      player.settlementsLeft--;
      if (state.setup.index >= PLAYER_COUNT) {
        const gain = emptyResources();
        for (const h of state.board.vertices[action.vertex].hexes) {
          const r = terrainResource(state.board.hexes[h].terrain);
          if (r && state.bank[r] > 0) {
            gain[r]++;
            state.bank[r]--;
          }
        }
        for (const r of RESOURCES) player.resources[r] += gain[r];
        state.log.push(`${name} places a settlement and collects ${describe(gain)}.`);
      } else {
        state.log.push(`${name} places a settlement.`);
      }
      state.setup.step = 'road';
      state.setup.lastSettlement = action.vertex;
      return;
    }

    case 'placeRoad': {
      if (state.phase !== 'setup' || state.setup.step !== 'road') throw new GameError(`Place a settlement now.`);
      requireTurn(state, me);
      if (!validRoadSpots(state, me).includes(action.edge)) {
        throw new GameError(`The road must touch the settlement you just placed.`);
      }
      state.roads[action.edge] = me;
      player.roadsLeft--;
      state.log.push(`${name} places a road.`);
      state.setup.index++;
      state.setup.step = 'settlement';
      state.setup.lastSettlement = null;
      if (state.setup.index >= state.setup.order.length) {
        state.phase = 'main';
        state.currentPlayer = state.setup.order[0];
        state.turn = 1;
        state.turnStep = 'roll';
        state.log.push(`Setup done. ${state.players[state.currentPlayer].name} rolls first.`);
      } else {
        state.currentPlayer = state.setup.order[state.setup.index];
      }
      return;
    }

    case 'rollDice': {
      requireTurn(state, me);
      requireStep(state, 'roll');
      const dice: [number, number] = [rng.int(6) + 1, rng.int(6) + 1];
      const roll = dice[0] + dice[1];
      state.dice = dice;
      state.log.push(`${name} rolls ${roll}.`);
      if (roll === 7) {
        state.pendingDiscards = {};
        state.players.forEach((p, i) => {
          const n = totalCards(p.resources);
          if (n > 7) state.pendingDiscards[i] = Math.floor(n / 2);
        });
        state.afterRobber = 'actions';
        state.turnStep = Object.keys(state.pendingDiscards).length ? 'discard' : 'moveRobber';
      } else {
        produce(state, roll);
        state.turnStep = 'actions';
      }
      return;
    }

    case 'discard': {
      requireStep(state, 'discard');
      const owed = state.pendingDiscards[me];
      if (owed === undefined) throw new GameError(`You don't need to discard.`);
      const cards = toCounts(action.resources);
      if (totalCards(cards) !== owed) throw new GameError(`Discard exactly ${owed} cards.`);
      if (!hasAll(player.resources, cards)) throw new GameError(`You don't have those cards.`);
      for (const r of RESOURCES) {
        player.resources[r] -= cards[r];
        state.bank[r] += cards[r];
      }
      delete state.pendingDiscards[me];
      state.log.push(`${name} discards ${owed} cards.`);
      if (Object.keys(state.pendingDiscards).length === 0) state.turnStep = 'moveRobber';
      return;
    }

    case 'moveRobber': {
      requireTurn(state, me);
      requireStep(state, 'moveRobber');
      if (!state.board.hexes[action.hex]) throw new GameError(`Unknown tile.`);
      if (action.hex === state.robberHex) throw new GameError(`The robber has to move to a different tile.`);
      const victims = robberVictims(state, me, action.hex);
      if (victims.length > 0 && (action.victim === null || !victims.includes(action.victim))) {
        throw new GameError(`Choose a player to steal from.`);
      }
      if (victims.length === 0 && action.victim !== null) throw new GameError(`Nobody there to steal from.`);
      state.robberHex = action.hex;
      state.log.push(`${name} moves the robber.`);
      if (action.victim !== null) {
        const target = state.players[action.victim];
        const pool = RESOURCES.flatMap((r) => Array<Resource>(target.resources[r]).fill(r));
        const stolen = pool[rng.int(pool.length)];
        target.resources[stolen]--;
        player.resources[stolen]++;
        state.log.push(`${name} steals a card from ${target.name}.`);
      }
      state.turnStep = state.afterRobber;
      return;
    }

    case 'buildRoad': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      if (player.roadsLeft <= 0) throw new GameError(`You have no roads left.`);
      if (!canPlaceRoad(state, me, action.edge)) throw new GameError(`You can't build a road there.`);
      if (state.freeRoads > 0) state.freeRoads--;
      else pay(state, me, COSTS.road, 'a road');
      state.roads[action.edge] = me;
      player.roadsLeft--;
      state.log.push(`${name} builds a road.`);
      updateLongestRoad(state);
      return;
    }

    case 'buildSettlement': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      if (player.settlementsLeft <= 0) throw new GameError(`You have no settlements left.`);
      if (!canPlaceSettlement(state, me, action.vertex, true)) throw new GameError(`You can't build a settlement there.`);
      pay(state, me, COSTS.settlement, 'a settlement');
      state.buildings[action.vertex] = { player: me, type: 'settlement' };
      player.settlementsLeft--;
      state.log.push(`${name} builds a settlement.`);
      updateLongestRoad(state);
      return;
    }

    case 'buildCity': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      const b = state.buildings[action.vertex];
      if (!b || b.player !== me || b.type !== 'settlement') throw new GameError(`Cities go on one of your settlements.`);
      if (player.citiesLeft <= 0) throw new GameError(`You have no cities left.`);
      pay(state, me, COSTS.city, 'a city');
      b.type = 'city';
      player.citiesLeft--;
      player.settlementsLeft++;
      state.log.push(`${name} builds a city.`);
      return;
    }

    case 'buyDevCard': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      if (state.devDeck.length === 0) throw new GameError(`No development cards left.`);
      pay(state, me, COSTS.devCard, 'a development card');
      player.devCards.push({ type: state.devDeck.pop()!, boughtOnTurn: state.turn });
      state.log.push(`${name} buys a development card.`);
      return;
    }

    case 'playKnight': {
      requireTurn(state, me);
      requireStep(state, 'roll', 'actions');
      takeDevCard(state, me, 'knight');
      player.knightsPlayed++;
      state.log.push(`${name} plays a knight.`);
      const army = state.largestArmy;
      if (player.knightsPlayed >= 3 && (!army || army.player === me || player.knightsPlayed > army.size)) {
        if (army?.player !== me) state.log.push(`${name} takes Largest Army (${player.knightsPlayed}).`);
        state.largestArmy = { player: me, size: player.knightsPlayed };
      }
      state.afterRobber = state.turnStep;
      state.turnStep = 'moveRobber';
      return;
    }

    case 'playRoadBuilding': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      takeDevCard(state, me, 'roadBuilding');
      state.freeRoads = Math.min(2, player.roadsLeft);
      state.log.push(`${name} plays Road Building.`);
      return;
    }

    case 'playYearOfPlenty': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      const want = emptyResources();
      for (const r of action.resources) {
        if (!RESOURCES.includes(r)) throw new GameError(`Unknown resource.`);
        want[r]++;
      }
      if (!hasAll(state.bank, want)) throw new GameError(`The bank doesn't have those.`);
      takeDevCard(state, me, 'yearOfPlenty');
      for (const r of RESOURCES) {
        player.resources[r] += want[r];
        state.bank[r] -= want[r];
      }
      state.log.push(`${name} plays Year of Plenty and takes ${describe(want)}.`);
      return;
    }

    case 'playMonopoly': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      if (!RESOURCES.includes(action.resource)) throw new GameError(`Unknown resource.`);
      takeDevCard(state, me, 'monopoly');
      let taken = 0;
      state.players.forEach((p, i) => {
        if (i === me) return;
        taken += p.resources[action.resource];
        p.resources[action.resource] = 0;
      });
      player.resources[action.resource] += taken;
      state.log.push(`${name} plays Monopoly on ${action.resource} and takes ${taken}.`);
      return;
    }

    case 'bankTrade': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      if (!RESOURCES.includes(action.give) || !RESOURCES.includes(action.get)) throw new GameError(`Unknown resource.`);
      if (action.give === action.get) throw new GameError(`Pick two different resources.`);
      const ratio = tradeRatio(state, me, action.give);
      if (player.resources[action.give] < ratio) throw new GameError(`You need ${ratio} ${action.give}.`);
      if (state.bank[action.get] < 1) throw new GameError(`The bank is out of ${action.get}.`);
      player.resources[action.give] -= ratio;
      state.bank[action.give] += ratio;
      player.resources[action.get]++;
      state.bank[action.get]--;
      state.log.push(`${name} trades ${ratio} ${action.give} for 1 ${action.get} with the bank.`);
      return;
    }

    case 'offerTrade': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      const give = toCounts(action.give);
      const get = toCounts(action.get);
      if (totalCards(give) === 0 || totalCards(get) === 0) throw new GameError(`A trade needs something on both sides.`);
      if (RESOURCES.some((r) => give[r] > 0 && get[r] > 0)) throw new GameError(`Don't put the same resource on both sides.`);
      if (!hasAll(player.resources, give)) throw new GameError(`You don't have those cards.`);
      state.trade = { from: me, give, get, responses: {} };
      state.log.push(`${name} offers ${describe(give)} for ${describe(get)}.`);
      return;
    }

    case 'respondTrade': {
      const trade = state.trade;
      if (!trade) throw new GameError(`There's no trade on the table.`);
      if (me === trade.from) throw new GameError(`That's your own offer.`);
      if (action.accept && !hasAll(player.resources, trade.get)) throw new GameError(`You don't have those cards.`);
      trade.responses[me] = action.accept;
      state.log.push(`${name} ${action.accept ? 'accepts' : 'declines'} the trade.`);
      return;
    }

    case 'confirmTrade': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      const trade = state.trade;
      if (!trade) throw new GameError(`There's no trade on the table.`);
      if (trade.responses[action.with] !== true) throw new GameError(`That player hasn't accepted.`);
      const other = state.players[action.with];
      if (!hasAll(player.resources, trade.give) || !hasAll(other.resources, trade.get)) {
        throw new GameError(`Someone no longer has the cards for this trade.`);
      }
      for (const r of RESOURCES) {
        player.resources[r] += trade.get[r] - trade.give[r];
        other.resources[r] += trade.give[r] - trade.get[r];
      }
      state.trade = null;
      state.log.push(`${name} trades with ${other.name}.`);
      return;
    }

    case 'cancelTrade': {
      requireTurn(state, me);
      if (!state.trade) throw new GameError(`There's no trade on the table.`);
      state.trade = null;
      state.log.push(`${name} withdraws the trade.`);
      return;
    }

    case 'endTurn': {
      requireTurn(state, me);
      requireStep(state, 'actions');
      state.trade = null;
      state.freeRoads = 0;
      state.devCardPlayedThisTurn = false;
      state.dice = null;
      state.currentPlayer = (me + 1) % PLAYER_COUNT;
      state.turn++;
      state.turnStep = 'roll';
      state.log.push(`${state.players[state.currentPlayer].name}'s turn.`);
      return;
    }

    default: {
      const unknown: never = action;
      throw new GameError(`Unknown action ${(unknown as { type: string }).type}.`);
    }
  }
}
